import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mode, setMode] = useState("signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const isRegister = mode === "register";

  const switchMode = () => {
    setError("");
    setMode(isRegister ? "signin" : "register");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (isRegister && password.length < 6) {
      setError(t("login.passwordMin"));
      return;
    }
    setLoading(true);
    const res = isRegister ? await register(name, email, password) : await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  return (
    <div className="flex min-h-screen w-full bg-[#0b1626]">
      <div className="hidden flex-1 flex-col justify-between p-14 lg:flex">
        <div className="font-display text-3xl font-800 text-white">BHF</div>
        <div className="max-w-md">
          <h1 className="font-display text-4xl font-700 leading-tight text-white">
            {t("login.heroTitle")}
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-slate-400">
            {t("login.heroDesc")}
          </p>
        </div>
        <div className="text-xs text-slate-500">{t("login.footer")}</div>
      </div>
      <div className="flex w-full flex-col items-center justify-center bg-white px-6 lg:w-[460px] lg:shrink-0">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden font-display text-2xl font-800 text-slate-900">BHF</div>
          <h2 className="font-display text-2xl font-700 text-slate-900">{isRegister ? t("login.createAccount") : t("login.signIn")}</h2>
          <p className="mt-1 text-sm text-slate-500">{isRegister ? t("login.registerSubtitle") : t("login.subtitle")}</p>
          <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="login-form">
            {isRegister && (
              <div>
                <Label htmlFor="name">{t("login.name")}</Label>
                <Input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} required placeholder={t("login.namePlaceholder")} className="mt-1.5" data-testid="register-name-input" />
              </div>
            )}
            <div>
              <Label htmlFor="email">{t("login.email")}</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password">{t("login.password")}</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5" data-testid="login-password-input" />
            </div>
            {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} className="bhf-btn-primary w-full text-white border-0" data-testid={isRegister ? "register-submit-button" : "login-submit-button"}>
              {loading ? (isRegister ? t("login.registering") : t("login.signingIn")) : (isRegister ? t("login.register") : t("login.signIn"))}
            </Button>
          </form>
          <div className="mt-6 text-center text-sm text-slate-500">
            {isRegister ? t("login.haveAccount") : t("login.noAccount")}{" "}
            <button type="button" onClick={switchMode} className="font-semibold text-indigo-600 hover:text-indigo-700" data-testid="toggle-auth-mode">
              {isRegister ? t("login.backToSignIn") : t("login.signUp")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
